import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  ToggleButton,
  ToggleButtonGroup,
  Grid,
  Stack,
  Typography,
} from '@mui/material';
import {
  Apartment as ApartmentIcon,
  Bed as BedIcon,
  Bathtub as BathtubIcon,
  Straighten as StraightenIcon,
  Euro as EuroIcon,
} from '@mui/icons-material';
import { getApiUrl } from '../utils/apiConfig';

const ImmobilienAngebotePage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [properties, setProperties] = useState([]);
  const [angebotstyp, setAngebotstyp] = useState('kauf');

  useEffect(() => {
    let active = true;

    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axios.get(getApiUrl('/api/public/properties'));
        if (!active) return;
        setProperties(response.data.properties || []);
      } catch (err) {
        if (!active) return;
        setError('Immobilienangebote konnten nicht geladen werden.');
      } finally {
        if (active) setLoading(false);
      }
    };

    load();
    return () => {
      active = false;
    };
  }, []);

  const mappedApiProperties = properties.map((property) => {
    const firstImage = Array.isArray(property.fotos) && property.fotos.length > 0 ? property.fotos[0] : null;
    const coverImage = Array.isArray(property.fotos)
      ? property.fotos.find((img) => img?.url === property.cover_image_url)
      : null;
    return {
      id: property.id,
      titel: property.titel,
      untertitel: property.untertitel,
      teaser: property.card_teaser || '',
      adresse: property.adresse,
      wohnflaeche: property.wohnflaeche,
      zimmer: property.zimmer,
      baeder: property.baeder || 1,
      vermarktungsart: property.vermarktungsart || 'kauf',
      objektart: property.objektart || 'wohnung',
      objektunterart: property.objektunterart || 'etagenwohnung',
      zustand: property.zustand || 'gepflegt',
      lagekategorie: property.lagekategorie || 'zentrumsnah',
      preis: property.kaufpreis ? `${Number(property.kaufpreis).toLocaleString('de-DE')} EUR` : 'Kaufpreis auf Anfrage',
      imageUrl: coverImage?.url || firstImage?.url || 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80',
      slug: property.slug,
      isApi: true,
    };
  });

  const kaufangebote = mappedApiProperties.filter((property) => property.vermarktungsart !== 'miete');
  const mietangebote = mappedApiProperties.filter((property) => property.vermarktungsart === 'miete');

  const angezeigteAngebote = angebotstyp === 'miete' ? mietangebote : kaufangebote;

  return (
    <Box sx={{ bgcolor: '#f8fafc', minHeight: '100vh', py: { xs: 4, md: 6 } }}>
      <Container maxWidth="lg">
        <Stack spacing={3}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h3" sx={{ fontWeight: 800, color: '#1a365d', mb: 1 }}>
              Immobilienangebote
            </Typography>
            <Typography variant="h6" sx={{ color: '#4a5568', fontWeight: 400 }}>
              Waehlen Sie direkt zwischen Kauf- und Mietangeboten mit allen wichtigsten Rahmendaten auf einen Blick.
            </Typography>
            <Box sx={{ mt: 2.5, display: 'flex', justifyContent: 'center' }}>
              <ToggleButtonGroup
                exclusive
                value={angebotstyp}
                onChange={(_, value) => value && setAngebotstyp(value)}
                size="small"
                color="primary"
              >
                <ToggleButton value="kauf">Kauf</ToggleButton>
                <ToggleButton value="miete">Miete</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {loading && (
            <Box sx={{ display: 'grid', placeItems: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          )}

          {!loading && error && (
            <Alert severity="error">
              {error}
            </Alert>
          )}

          {!loading && !error && angezeigteAngebote.length === 0 && (
            <Alert severity="info">
              {angebotstyp === 'miete'
                ? 'Aktuell sind keine Mietangebote verfügbar. Neue Objekte erscheinen automatisch, sobald sie veröffentlicht sind.'
                : 'Aktuell sind keine Kaufangebote verfügbar. Neue Objekte erscheinen automatisch, sobald sie veröffentlicht sind.'}
            </Alert>
          )}

          <Grid container spacing={3}>
            {angezeigteAngebote.map((property) => {

              return (
                <Grid key={property.id} item xs={12} md={6}>
                  <Card
                    sx={{ borderRadius: 3, height: '100%', cursor: property.slug || property.targetSlug ? 'pointer' : 'default' }}
                    onClick={() => {
                      const detailSlug = property.slug || property.targetSlug;
                      if (detailSlug) {
                        navigate(`/objekt/${detailSlug}`);
                      }
                    }}
                  >
                    <Box
                      component="img"
                      src={property.imageUrl}
                      alt={property.titel}
                      sx={{ width: '100%', height: 230, objectFit: 'cover' }}
                    />
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1a365d' }}>
                          {property.titel}
                        </Typography>
                        {property.untertitel && (
                          <Typography variant="body1" sx={{ color: '#4a5568' }}>
                            {property.untertitel}
                          </Typography>
                        )}
                        {property.teaser && (
                          <Typography variant="body2" sx={{ color: '#64748b' }}>
                            {property.teaser}
                          </Typography>
                        )}
                        <Typography variant="body2" sx={{ color: '#64748b' }}>
                          {property.adresse}
                        </Typography>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                          {property.objektart && <Chip label={property.objektart} size="small" />}
                          {property.objektunterart && <Chip label={property.objektunterart} size="small" variant="outlined" />}
                          {property.zustand && <Chip label={property.zustand} size="small" variant="outlined" />}
                          {property.lagekategorie && <Chip label={property.lagekategorie} size="small" variant="outlined" />}
                        </Stack>
                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                          {property.wohnflaeche && <Chip icon={<StraightenIcon />} label={`${property.wohnflaeche} m2`} variant="outlined" />}
                          {property.zimmer && <Chip icon={<BedIcon />} label={`${property.zimmer} Zimmer`} variant="outlined" />}
                          {property.baeder && <Chip icon={<BathtubIcon />} label={`${property.baeder} Bad`} variant="outlined" />}
                          <Chip icon={<ApartmentIcon />} label={angebotstyp === 'miete' ? 'Mietobjekt' : 'Kaufobjekt'} variant="outlined" />
                        </Stack>
                        <Typography variant="h6" sx={{ fontWeight: 800, color: '#00acc1', display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <EuroIcon fontSize="small" /> {property.preis}
                        </Typography>
                        <Box>
                          <Button
                            variant="contained"
                            onClick={(event) => {
                              event.stopPropagation();
                              const detailSlug = property.slug || property.targetSlug;
                              if (detailSlug) {
                                navigate(`/objekt/${detailSlug}`);
                                return;
                              }
                              navigate('/kontakt?source=immobilien&anliegen=Objektanfrage');
                            }}
                            sx={{ textTransform: 'none', fontWeight: 700 }}
                          >
                            Exposé anfordern
                          </Button>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        </Stack>
      </Container>
    </Box>
  );
};

export default ImmobilienAngebotePage;
